import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/cn'
import { useTestingResults, persistFlag, type TestingResult } from '@/hooks/useTestingResults'
import { useAthleteReport } from '@/hooks/useAthleteReport'
import { useAthlete } from '@/hooks/useAthlete'
import { LinkValdProfile } from '@/components/LinkValdProfile'
import { TESTING_METRICS, LOWER_IS_BETTER, type TestMetric } from '@/lib/testingMetrics'
import {
  bestPerSession, rowAsymmetry, formatAsymmetry, coefficientOfVariation,
  changeFromBaseline, findDsiPair, addAbdRatio, estimatedBodyMass, evaluateFlags,
  daysSince, STALE_DAYS, CV_NOISY_THRESHOLD, type FlagStatus,
} from '@/lib/testingAnalytics'

const round = (v: number, dp = 2) => Math.round(v * 10 ** dp) / 10 ** dp
const fmt = (v: number | null | undefined, unit: string) =>
  v == null || Number.isNaN(v) ? '—' : `${round(v)}${unit}`

const FLAG_STYLE: Record<FlagStatus, string> = {
  red: 'border-danger/50 bg-danger/10 text-danger',
  amber: 'border-gold/50 bg-gold/10 text-gold',
  green: 'border-success/50 bg-success/10 text-success',
  none: 'border-border bg-surface-2 text-muted',
}

/** Tiny trend line across stored sessions (oldest → newest). */
function Sparkline({ values, lowerIsBetter = false }: { values: number[]; lowerIsBetter?: boolean }) {
  if (values.length < 2) return null
  const w = 64
  const h = 18
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const pts = values
    .map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / span) * h}`)
    .join(' ')
  const rising = values[values.length - 1] >= values[0]
  const improving = lowerIsBetter ? !rising : rising
  return (
    <svg width={w} height={h} className="mt-1 block" aria-hidden="true">
      <polyline
        points={pts}
        fill="none"
        strokeWidth="1.5"
        className={improving ? 'stroke-success' : 'stroke-danger'}
      />
    </svg>
  )
}

export function TestingPanel({ athleteId, isCoach }: { athleteId: string; isCoach: boolean }) {
  const q = useTestingResults(athleteId)
  const all = useMemo(() => q.data ?? [], [q.data])
  // One row per test per date, all-dash rows dropped (brief task 1).
  const rows = useMemo(() => bestPerSession(all), [all])
  const summary = useMemo(() => evaluateFlags({ rows }), [rows])
  // Gym personal bests, so this tab still has good news when testing is flat.
  const liftsQ = useAthleteReport(athleteId)
  const athleteQ = useAthlete(athleteId, isCoach)
  const notLinked = isCoach && athleteQ.isSuccess && !athleteQ.data?.vald_profile_id
  const pbs = liftsQ.data?.recentPBs ?? []

  // Write the computed flag back so it's queryable in Supabase, not just shown
  // here. Coach-only, and only when it actually differs from what's stored.
  const latestRow = rows[0]
  useEffect(() => {
    if (!isCoach || !latestRow) return
    if (
      latestRow.flag_status === summary.flag.status &&
      latestRow.flag_reason === summary.flag.reason
    ) return
    void persistFlag(latestRow.id, summary.flag.status, summary.flag.reason)
  }, [isCoach, latestRow, summary.flag.status, summary.flag.reason])

  if (q.isLoading) return <p className="py-10 text-center text-muted">Loading test results…</p>
  if (q.isError) return <p className="py-10 text-center text-danger">Couldn’t load test results.</p>

  const tested = new Set(rows.map((r) => r.test_id))
  const neverTested = TESTING_METRICS.filter((m) => !tested.has(m.id))

  if (rows.length === 0) {
    return (
      <div className="space-y-4">
        {/* Unlinked athletes never sync, however often the job runs. */}
        {notLinked && (
          <LinkValdProfile athleteId={athleteId} athleteName={athleteQ.data?.name ?? 'This athlete'} />
        )}
        {/* Never tested, but their gym work still deserves to be seen. */}
        {notLinked && (
        <LinkValdProfile athleteId={athleteId} athleteName={athleteQ.data?.name ?? 'This athlete'} />
      )}

      {pbs.length > 0 && <PbStrip pbs={pbs} />}
        <div className="py-12 text-center">
          <p className="font-display text-xl tracking-wide text-subtle">No test results yet</p>
          <p className="mt-2 text-sm text-muted">
            {isCoach
              ? 'Results appear here automatically once VALD testing syncs.'
              : 'Your coach will add testing data here after each assessment.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary strip */}
      <div className={cn('rounded-card border px-4 py-3', FLAG_STYLE[summary.flag.status])}>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-condensed text-sm font-bold uppercase tracking-[0.15em]">
            {summary.flag.status === 'none' ? '○' : summary.flag.status === 'green' ? '✓' : '▲'}{' '}
            {summary.flag.reason}
          </span>
          {summary.lastTestDate && (
            <span className="text-xs opacity-80">
              last tested {daysSince(summary.lastTestDate)} days ago
            </span>
          )}
        </div>
        <p className="mt-1 text-xs opacity-80">
          {neverTested.length > 0
            ? `${neverTested.length} of ${TESTING_METRICS.length} tests never completed`
            : 'Full battery completed'}
          {summary.dsiValue != null && ` · DSI ${round(summary.dsiValue)}`}
        </p>
        {summary.notes.map((n, i) => (
          <p key={i} className="mt-1 text-[11px] opacity-75">{n}</p>
        ))}
      </div>

      {pbs.length > 0 && <PbStrip pbs={pbs} />}

      {TESTING_METRICS.map((metric) => (
        <MetricSection
          key={metric.id}
          metric={metric}
          sessions={rows.filter((r) => r.test_id === metric.id)}
          allRows={rows}
          isCoach={isCoach}
        />
      ))}
    </div>
  )
}

function MetricSection({
  metric, sessions, allRows, isCoach,
}: {
  metric: TestMetric
  sessions: TestingResult[]
  allRows: TestingResult[]
  isCoach: boolean
}) {
  const [showInfo, setShowInfo] = useState(false)
  const latest = sessions[0]
  const previous = sessions[1]
  const baseline = sessions[sessions.length - 1]
  const stale = latest ? daysSince(latest.date) > STALE_DAYS : false

  // Derived tiles for this test type.
  const derived: { label: string; value: string; note?: string }[] = []
  if (metric.id === 'cmj') {
    const pair = findDsiPair(allRows)
    if (pair) {
      derived.push({
        label: 'Dynamic Strength Index',
        value: String(round(pair.value)),
        note:
          pair.value > 0.8 ? 'Prioritise max strength'
          : pair.value < 0.6 ? 'Prioritise ballistic work'
          : 'Balanced profile',
      })
    }
  }
  if (metric.id === 'imtp' && latest) {
    const bm = estimatedBodyMass(latest.values?.peak_force, latest.values?.relative_force)
    if (bm != null) derived.push({ label: 'Body mass', value: `${round(bm, 1)}kg`, note: 'estimated' })
  }
  if ((metric.id === 'hip_abd' || metric.id === 'hip_add') && latest) {
    const abd = allRows.find((r) => r.test_id === 'hip_abd')
    const add = allRows.find((r) => r.test_id === 'hip_add')
    for (const side of ['left', 'right'] as const) {
      const ratio = addAbdRatio(add?.values?.[side], abd?.values?.[side])
      if (ratio != null) {
        derived.push({
          label: `Add:Abd ${side === 'left' ? 'L' : 'R'}`,
          value: String(round(ratio)),
          note: ratio < 0.9 ? 'Adductor deficit' : undefined,
        })
      }
    }
  }

  return (
    <section>
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-1.5">
        <h3 className="font-display text-lg tracking-wide text-gold">{metric.label}</h3>
        {latest && (
          <span
            className={cn(
              'rounded-full px-2 py-0.5 font-condensed text-[10px] uppercase tracking-[0.1em]',
              stale ? 'bg-surface-3 text-subtle' : 'bg-surface-2 text-muted'
            )}
          >
            {daysSince(latest.date)}d ago{stale ? ' · stale' : ''}
          </span>
        )}
        <button
          onClick={() => setShowInfo((v) => !v)}
          className="ml-auto font-condensed text-[11px] uppercase tracking-[0.15em] text-subtle hover:text-gold"
        >
          {showInfo ? 'Hide' : 'What is this?'}
        </button>
      </div>

      {showInfo && (
        <div className="mt-2 rounded-btn bg-surface-2 px-3.5 py-3 animate-fade-in">
          <p className="text-[13px] leading-relaxed text-text">{metric.plain}</p>
          {isCoach && (
            <p className="mt-2 border-t border-border pt-2 text-[12px] leading-relaxed text-muted">
              {metric.description}
            </p>
          )}
        </div>
      )}

      {!latest ? (
        <p className="mt-2 text-xs italic text-subtle">
          Not yet tested — part of the standard battery.
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {metric.fields.map((f) => {
              const unit = f.unit ?? metric.unit
              const isAsym = f.key === 'asymmetry'
              // Single-leg CMJ has no synced asymmetry — compute it from limbs.
              const now = isAsym ? rowAsymmetry(latest) : (latest.values?.[f.key] ?? null)
              const before = isAsym ? rowAsymmetry(previous) : (previous?.values?.[f.key] ?? null)
              const first = isAsym ? rowAsymmetry(baseline) : (baseline?.values?.[f.key] ?? null)
              const series = sessions
                .map((s) => (isAsym ? rowAsymmetry(s) : s.values?.[f.key] ?? null))
                .filter((v): v is number => v != null)
                // Asymmetry is signed for side; the trend is about magnitude.
                .map((v) => (isAsym ? Math.abs(v) : v))
                .reverse()
              const cv = coefficientOfVariation(series)
              const delta = now != null && before != null ? now - before : null
              const base = changeFromBaseline(now, first)
              const warn = isAsym && now != null && Math.abs(now) >= 15
              return (
                <div
                  key={f.key}
                  className={cn(
                    'min-w-[116px] flex-1 rounded-card border bg-surface-2 px-3 py-2',
                    warn ? 'border-danger/50' : 'border-border'
                  )}
                >
                  <p className="font-condensed text-[10px] uppercase tracking-[0.15em] text-muted">
                    {f.label}
                  </p>
                  <p className={cn('font-display text-xl tracking-wide', warn ? 'text-danger' : 'text-text')}>
                    {isAsym ? formatAsymmetry(now) : fmt(now, unit)}
                  </p>
                  {delta != null && Math.abs(delta) >= 0.01 && (
                    <p className="text-[11px] text-subtle">
                      {delta > 0 ? '▲' : '▼'} {round(Math.abs(delta))}{unit} vs last
                    </p>
                  )}
                  {base != null && Math.abs(base) >= 0.1 && (
                    <p className="text-[11px] text-subtle">
                      {base > 0 ? '▲' : '▼'} {round(Math.abs(base), 1)}% vs baseline
                    </p>
                  )}
                  {cv != null && (
                    <p className={cn('text-[10px]', cv > CV_NOISY_THRESHOLD ? 'text-danger' : 'text-subtle')}>
                      CV {round(cv, 1)}%{cv > CV_NOISY_THRESHOLD ? ' — noisy' : ''}
                    </p>
                  )}
                  <Sparkline values={series} lowerIsBetter={LOWER_IS_BETTER.has(f.key)} />
                </div>
              )
            })}

            {derived.map((d) => (
              <div key={d.label} className="min-w-[116px] flex-1 rounded-card border border-data/40 bg-data/[.06] px-3 py-2">
                <p className="font-condensed text-[10px] uppercase tracking-[0.15em] text-data">{d.label}</p>
                <p className="font-display text-xl tracking-wide text-text">{d.value}</p>
                {d.note && <p className="text-[11px] text-muted">{d.note}</p>}
              </div>
            ))}
          </div>

          {sessions.length > 1 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="font-condensed text-[10px] uppercase tracking-[0.15em] text-muted">
                    <th className="border-b border-border px-2 py-1.5 text-left">Date</th>
                    {metric.fields.map((f) => (
                      <th key={f.key} className="border-b border-border px-2 py-1.5 text-center">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((e, i) => (
                    <tr key={e.id} className={cn('border-b border-border/50', i === 0 ? 'text-text' : 'text-muted')}>
                      <td className="whitespace-nowrap px-2 py-1.5">
                        {new Date(e.date).toLocaleDateString()}
                        {i === 0 && (
                          <span className="ml-1.5 font-condensed text-[9px] tracking-[0.15em] text-gold">LATEST</span>
                        )}
                      </td>
                      {metric.fields.map((f) => (
                        <td key={f.key} className="px-2 py-1.5 text-center">
                          {f.key === 'asymmetry'
                            ? formatAsymmetry(rowAsymmetry(e))
                            : fmt(e.values?.[f.key], f.unit ?? metric.unit)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}


/** Gym personal bests — shown on the testing tab so there's good news even when
 *  force-plate numbers are flat or the athlete has never been tested. */
function PbStrip({ pbs }: { pbs: { exerciseId: string; name: string; best: number; gain: number | null }[] }) {
  return (
    <div className="rounded-card border border-success/35 bg-success/[.08] px-4 py-3">
      <p className="font-condensed text-sm font-bold uppercase tracking-[0.12em] text-success">
        🏆 {pbs.length} personal best{pbs.length === 1 ? '' : 's'} in the gym this month
      </p>
      <ul className="mt-1.5 space-y-0.5">
        {pbs.slice(0, 4).map((l) => (
          <li key={l.exerciseId} className="text-[13px] text-muted">
            <span className="text-text">{l.name}</span> — {round(l.best, 1)}kg
            <span className="text-success"> (▲{round(l.gain ?? 0, 1)}kg)</span>
          </li>
        ))}
      </ul>
      {pbs.length > 4 && <p className="mt-1 text-[11px] text-subtle">+{pbs.length - 4} more</p>}
    </div>
  )
}
